import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import AuthPage from "@/pages/AuthPage";
import OnboardingPage from "@/pages/OnboardingPage";
import TodayPage from "@/pages/TodayPage";
import LongevityPage from "@/pages/LongevityPage";
import WorkoutPage from "@/pages/WorkoutPage";
import NutritionPage from "@/pages/NutritionPage";
import RecoveryPage from "@/pages/RecoveryPage";
import ProfilePage from "@/pages/ProfilePage";
import SharePage from "@/pages/SharePage";
import CoachPage from "@/pages/CoachPage";
import CommunityPage from "@/pages/CommunityPage";
import AcademyPage from "@/pages/AcademyPage";
import WearablesPage from "@/pages/WearablesPage";
import CorporatePage from "@/pages/CorporatePage";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export { API };

function App() {
  const [token, setToken] = useState(localStorage.getItem("athlevity_token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const api = useCallback(() => {
    const instance = axios.create({ baseURL: API });
    if (token) {
      instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    return instance;
  }, [token]);

  useEffect(() => {
    if (token) {
      api()
        .get("/auth/me")
        .then((res) => {
          setUser(res.data);
          setNeedsOnboarding(!res.data.onboarding_completed);
          setLoading(false);
        })
        .catch(() => { localStorage.removeItem("athlevity_token"); setToken(null); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token, api]);

  const login = (newToken, userData) => {
    localStorage.setItem("athlevity_token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("athlevity_token");
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ token, user, setUser, login, logout, api }}>
      <BrowserRouter>
        <Toaster position="top-center" toastOptions={{ className: "glass-card !bg-[#16213e] !text-white !border-white/10" }} />
        {!token ? (
          <Routes>
            <Route path="*" element={<AuthPage />} />
          </Routes>
        ) : needsOnboarding ? (
          <OnboardingPage onComplete={() => setNeedsOnboarding(false)} />
        ) : (
          <Layout>
            <Routes>
              <Route path="/" element={<TodayPage />} />
              <Route path="/longevity" element={<LongevityPage />} />
              <Route path="/workout" element={<WorkoutPage />} />
              <Route path="/nutrition" element={<NutritionPage />} />
              <Route path="/recovery" element={<RecoveryPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/coach" element={<CoachPage />} />
              <Route path="/academy" element={<AcademyPage />} />
              <Route path="/wearables" element={<WearablesPage />} />
              <Route path="/corporate" element={<CorporatePage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/share" element={<SharePage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Layout>
        )}
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
